/**
 * CategoryHubTab — DRY shared component for the 3 category hub views in ManagePlatform:
 *   - stores-management (المتاجر): activation requests + active stores
 *   - agents-management (الوكلاء): requests + invitation codes + tasks + agents
 *   - listings-management (إدارة المعروضات): 6 listing types
 *
 * Each hub renders a header + a grid of clickable nav cards.
 * Pass `variant` to pick the right config.
 *
 * Extracted from ManagePlatform.js
 */
import { motion } from 'framer-motion';
import {
  Store, ClipboardList, Shield, UserPlus, Ticket, ShoppingCart,
  Building2, Car, Phone, CreditCard as PlateIcon,
} from 'lucide-react';
import MerchantListingsStatsBanner from './MerchantListingsStatsBanner';

const VARIANTS = {
  'stores-management': {
    headerIcon: Store,
    headerColor: 'text-orange-400',
    titleAr: 'المتاجر',
    titleEn: 'Stores',
    grid: 'grid-cols-1 sm:grid-cols-2',
    cards: ({ setActiveTab, pendingStores, stores, language }) => [
      {
        testid: 'btn-store-requests',
        onClick: () => setActiveTab('store-requests'),
        hoverBorder: 'hover:border-orange-500/50',
        iconBg: 'bg-orange-500/10',
        iconBorder: 'border-orange-500/20',
        icon: ClipboardList,
        iconColor: 'text-orange-400',
        titleAr: 'طلبات تفعيل المتاجر',
        titleEn: 'Store Activation Requests',
        badge: pendingStores?.length > 0 ? { count: pendingStores.length, bg: 'bg-orange-500' } : null,
      },
      {
        testid: 'btn-stores',
        delay: 0.1,
        onClick: () => setActiveTab('stores'),
        hoverBorder: 'hover:border-emerald-500/50',
        iconBg: 'bg-emerald-500/10',
        iconBorder: 'border-emerald-500/20',
        icon: Store,
        iconColor: 'text-emerald-400',
        titleAr: 'المتاجر',
        titleEn: 'Stores',
        subtitle: `${stores.filter((s) => s.platform === 'cloudgm_business' && s.status === 'active').length} ${language === 'ar' ? 'فعال' : 'active'}`,
      },
    ],
  },
  'agents-management': {
    headerIcon: Shield,
    headerColor: 'text-purple-400',
    titleAr: 'الوكلاء',
    titleEn: 'Agents',
    grid: 'grid-cols-1 sm:grid-cols-2',
    cards: ({ setActiveTab, agentRequests, invitationCodes, approvedAgents, language }) => [
      {
        testid: 'btn-agent-requests',
        onClick: () => setActiveTab('agent-requests'),
        hoverBorder: 'hover:border-purple-500/50',
        iconBg: 'bg-purple-500/10',
        iconBorder: 'border-purple-500/20',
        icon: UserPlus,
        iconColor: 'text-purple-400',
        titleAr: 'طلبات عضوية وكيل',
        titleEn: 'Agent Membership Requests',
        badge: (() => {
          const c = agentRequests.filter((r) => r.status === 'pending').length;
          return c > 0 ? { count: c, bg: 'bg-purple-500' } : null;
        })(),
      },
      {
        testid: 'btn-invite-codes',
        delay: 0.1,
        onClick: () => setActiveTab('invitation-codes'),
        hoverBorder: 'hover:border-amber-500/50',
        iconBg: 'bg-amber-500/10',
        iconBorder: 'border-amber-500/20',
        icon: Ticket,
        iconColor: 'text-amber-400',
        titleAr: 'دعوات الوكلاء',
        titleEn: 'Agent Invites',
        subtitle: (() => {
          const c = invitationCodes.filter((x) => x.is_active).length;
          return c > 0 ? `${c} ${language === 'ar' ? 'دعوة فعالة' : 'active invites'}` : null;
        })(),
      },
      {
        testid: 'btn-agent-tasks',
        delay: 0.2,
        onClick: () => setActiveTab('agent-tasks'),
        hoverBorder: 'hover:border-orange-500/50',
        iconBg: 'bg-orange-500/10',
        iconBorder: 'border-orange-500/20',
        icon: ClipboardList,
        iconColor: 'text-orange-400',
        titleAr: 'مهام الوكلاء',
        titleEn: 'Agent Tasks',
      },
      {
        testid: 'btn-agents',
        delay: 0.3,
        onClick: () => setActiveTab('agents'),
        hoverBorder: 'hover:border-cyan-500/50',
        iconBg: 'bg-cyan-500/10',
        iconBorder: 'border-cyan-500/20',
        icon: Shield,
        iconColor: 'text-cyan-400',
        titleAr: 'وكلاء الشركة',
        titleEn: 'Company Agents',
        subtitle: approvedAgents.length > 0
          ? `${approvedAgents.length} ${language === 'ar' ? 'وكيل' : 'agents'}`
          : null,
      },
    ],
  },
  'listings-management': {
    headerIcon: ShoppingCart,
    headerColor: 'text-emerald-400',
    titleAr: 'إدارة المعروضات',
    titleEn: 'Listings Management',
    grid: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    cards: ({ setActiveTab }) => [
      { testid: 'btn-listings-cars', onClick: () => setActiveTab('listings-cars'), hoverBorder: 'hover:border-orange-500/50', iconBg: 'bg-orange-500/10', iconBorder: 'border-orange-500/20', icon: Car, iconColor: 'text-orange-400', titleAr: 'السيارات', titleEn: 'Cars' },
      { testid: 'btn-listings-car-plates', delay: 0.05, onClick: () => setActiveTab('listings-car-plates'), hoverBorder: 'hover:border-amber-500/50', iconBg: 'bg-amber-500/10', iconBorder: 'border-amber-500/20', icon: PlateIcon, iconColor: 'text-amber-400', titleAr: 'لوحات السيارات', titleEn: 'Car Plates' },
      { testid: 'btn-listings-numbers', delay: 0.1, onClick: () => setActiveTab('listings-numbers'), hoverBorder: 'hover:border-purple-500/50', iconBg: 'bg-purple-500/10', iconBorder: 'border-purple-500/20', icon: Phone, iconColor: 'text-purple-400', titleAr: 'أرقام الموبايل والهواتف', titleEn: 'Phone & Mobile Numbers' },
      { testid: 'btn-listings-real-estate', delay: 0.15, onClick: () => setActiveTab('listings-real-estate'), hoverBorder: 'hover:border-blue-500/50', iconBg: 'bg-blue-500/10', iconBorder: 'border-blue-500/20', icon: Building2, iconColor: 'text-blue-400', titleAr: 'العقارات', titleEn: 'Real Estate' },
      { testid: 'btn-listings-ecommerce', delay: 0.2, onClick: () => setActiveTab('listings-ecommerce'), hoverBorder: 'hover:border-emerald-500/50', iconBg: 'bg-emerald-500/10', iconBorder: 'border-emerald-500/20', icon: Store, iconColor: 'text-emerald-400', titleAr: 'المتاجر الإلكترونية', titleEn: 'E-Commerce Stores' },
    ],
  },
};

export default function CategoryHubTab({
  variant,
  language,
  setActiveTab,
  pendingStores = [],
  stores = [],
  agentRequests = [],
  invitationCodes = [],
  approvedAgents = [],
}) {
  const config = VARIANTS[variant];
  if (!config) return null;

  const HeaderIcon = config.headerIcon;
  const cards = config.cards({
    setActiveTab, pendingStores, stores, agentRequests, invitationCodes, approvedAgents, language,
  });

  return (
    <div data-testid={`category-hub-${variant}`}>
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <HeaderIcon className={`h-6 w-6 ${config.headerColor}`} />
        {language === 'ar' ? config.titleAr : config.titleEn}
      </h2>

      {/* Listings hub: stats summary banner with clickable shortcuts */}
      {variant === 'listings-management' && (
        <MerchantListingsStatsBanner language={language} onShortcut={setActiveTab} />
      )}

      <div className={`grid ${config.grid} gap-6`}>
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.testid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={card.delay ? { delay: card.delay } : undefined}
              whileHover={{ y: -5 }}
              onClick={card.onClick}
              className={`group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 ${card.hoverBorder} transition-all duration-300 cursor-pointer text-center`}
              data-testid={card.testid}
            >
              {card.badge && (
                <span className={`absolute top-3 left-3 ${card.badge.bg} text-white text-xs px-2 py-1 rounded-full font-bold`}>
                  {card.badge.count}
                </span>
              )}
              <div className={`w-14 h-14 ${card.iconBg} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border ${card.iconBorder}`}>
                <Icon className={`h-8 w-8 ${card.iconColor}`} />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? card.titleAr : card.titleEn}
              </h3>
              {card.subtitle && (
                <p className="text-xs text-[#71717A]">{card.subtitle}</p>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
